import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiChatTextLight } from 'react-icons/pi';
import { useAppDispatch } from '../store';
import { showFeedbackModal } from '../store/actions/UserFeedbackActions';
import DetailFrame from '../components/shared/Frames/DetailFrame';
import GlassCard from '../components/shared/brand/GlassCard';
import Button from '../components/shared/TailwindComponents/Button';
import Dropdown from '../components/shared/TailwindComponents/Dropdown';
import Textarea from '../components/shared/TailwindComponents/Textarea';
import FileUploader from '../components/shared/TailwindComponents/FileUploader';
import { validateFileSecure } from '../utils/security/fileSecurityValidator';
import MarketplaceGate from '../components/Marketplace/MarketplaceGate';
import { intakeCountryOptions } from '../components/Marketplace/marketplaceUi';
import { createLead, errorMessage, triageLead, uploadLeadDocuments } from '../services/api/marketplaceService';

const ISSUE_MIN = 10;
const ISSUE_MAX = 10000;
const MAX_DOCUMENTS = 10; // mirrors the backend cap per request

/**
 * Client intake: describe the issue (+ documents) → lead → triage → the request.
 *
 * Documents are uploaded to the request itself (POST /leads/<uuid>/documents),
 * checked here with validateFileSecure and again server-side by the matter
 * upload pipeline. A lawyer sees only how many there are until engagement; on
 * accept they are copied into the new matter.
 */
const NewRequestContent: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [issueText, setIssueText] = useState('');
  const [jurisdictionHint, setJurisdictionHint] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const warn = (message: string, subMsg?: string) =>
    dispatch(showFeedbackModal({ showModal: true, modalType: 'warning', duration: 6000, message, ...(subMsg ? { subMsg } : {}) }));

  /** Browser-side screen (magic bytes, type, size); the server re-checks everything. */
  const addFiles = async (next: File[]) => {
    const fresh = next.filter((f) => !files.includes(f));
    const checked = await Promise.all(fresh.map(async (f) => ({ f, r: await validateFileSecure(f) })));
    const rejected = checked.filter(({ r }) => !r.isValid).map(({ f }) => f.name);
    if (rejected.length) warn(t('Some files were not added'), rejected.join(', '));
    const accepted = checked.filter(({ r }) => r.isValid).map(({ f, r }) => r.processedFile ?? f);
    setFiles([...next.filter((f) => files.includes(f)), ...accepted].slice(0, MAX_DOCUMENTS));
  };

  const issueError =
    issueText.length > 0 && issueText.trim().length < ISSUE_MIN
      ? t('Describe your issue in at least {{count}} characters', { count: ISSUE_MIN })
      : undefined;

  const submit = async () => {
    const issue = issueText.trim();
    if (issue.length < ISSUE_MIN || issue.length > ISSUE_MAX) return;
    setSubmitting(true);
    try {
      const created = await createLead({
        issue_text: issue,
        ...(jurisdictionHint ? { jurisdiction_hint: jurisdictionHint } : {}),
      });
      if (files.length) {
        try {
          const result = await uploadLeadDocuments(created.uuid, files);
          if (result.skipped.length) {
            warn(t('Some documents were not attached'), result.skipped.map((x) => x.file_name).join(', '));
          }
        } catch (err) {
          // The request exists either way; documents can be re-sent from it.
          warn(t('Your documents could not be attached'), errorMessage(err, ''));
        }
      }
      await triageLead(created.uuid);
      history.push(`/marketplace/requests/${created.uuid}`);
    } catch (err) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'error',
        duration: 4000,
        message: errorMessage(err, t('Could not submit your request')),
      }));
      setSubmitting(false);
    }
  };

  return (
    <DetailFrame
      breadcrumbs={[
        { name: t('Marketplace'), href: '/marketplace', current: false },
        { name: t('New request'), href: '/marketplace/new', current: true },
      ]}
      onBack={() => history.push('/marketplace')}
    >
      <div className="details-canvas h-full w-full overflow-y-auto custom-scrollbar-main" data-testid="marketplace-new-request">
        <div className="details-pad mx-auto w-full max-w-3xl px-6 py-6">
          <GlassCard
            icon={<PiChatTextLight />}
            title={t('Describe your legal issue')}
            subtitle={t('PONS sorts your request by type and country and suggests lawyers who can help. PONS does not give legal advice.')}
          >
            <div className="space-y-4">
              <Textarea
                id="marketplace-issue"
                label={t('What do you need help with?')}
                placeholder={t('Describe what happened, what you want to achieve, and any deadlines.')}
                value={issueText}
                rows={7}
                required
                error={issueError}
                onChange={(e) => setIssueText(e.target.value.slice(0, ISSUE_MAX))}
              />
              <div className="space-y-1.5">
                <div className="text-sm text-fg-body">{t('Documents (optional)')}</div>
                <p className="text-[11px] text-fg-muted">
                  {t('Contracts, letters or notices. Lawyers see only how many you attached until you engage one.')}
                </p>
                <FileUploader files={files} onFilesChange={addFiles} maxFiles={MAX_DOCUMENTS} />
              </div>
              <Dropdown
                id="marketplace-jurisdiction"
                label={t('Country (optional)')}
                placeholder={t('Not sure')}
                helpText={t('Where it happened. Leave it empty if you are unsure; PONS works it out from your description.')}
                options={intakeCountryOptions(t)}
                value={jurisdictionHint}
                onChange={(v) => setJurisdictionHint(String(v || ''))}
                fullWidth
              />
              <div className="flex justify-end gap-2 border-t border-hairline-soft pt-4">
                <Button variant="outline" size="small" onClick={() => history.push('/marketplace')} disabled={submitting}>
                  {t('Cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={submit}
                  isLoading={submitting}
                  disabled={submitting || issueText.trim().length < ISSUE_MIN}
                >
                  {t('Submit request')}
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    </DetailFrame>
  );
};

const MarketplaceNewRequestPage: React.FC = () => (
  <MarketplaceGate>{() => <NewRequestContent />}</MarketplaceGate>
);

export default MarketplaceNewRequestPage;
